import { useMemo, useState } from "react";
import { ChefHat, ListChecks, Printer, ReceiptText } from "lucide-react";
import { Button, Checkbox, Modal } from "@a2z/ui";
import { useI18n, useTranslate } from "@/lib/i18n";
import { countedKey } from "@/lib/i18n/plural";
import { useMoney } from "@/shared/ui";
import type { Invoice } from "@/lib/api/types";
import {
  markablePlates,
  outstandingOnBill,
  partyShares,
  unmarkedPlates,
} from "@/modules/pos/lib/partyShares";
import { paidPlateIds } from "@/modules/pos/lib/settleSheet";
import { invoiceForPlates } from "@/modules/pos/lib/preBillCheck";
import { liveOpenTotals, plateNameForLocale } from "@/modules/pos/lib/groupPlates";

/**
 * THE PRINTER'S CHOICE (user, 2026-10-20) — where the bill's split lives now.
 *
 * The bill foot used to carry a «تقسيم الفاتورة» row on every bill. It left the
 * foot; splitting moved into the printer button. Tapping print asks one thing:
 *
 *  · **«الحساب كاملًا»** — today's check, unchanged (the page opens the
 *    pre-bill check dialog with its even split, or prints a closed bill's
 *    cheque).
 *  · **«اختيار أصناف»** — tick the plates, print a check for those alone. The
 *    page marks them as a PART of the bill first (B43's `assignParty`, the same
 *    marking the split sheet makes), so the part is paid the way a split part is
 *    paid today: its own share in the PAPER settle window's "by item"
 *    (`matchingParty`), or its chip in the dark till's part-payment sheet.
 *
 * And a bill that IS split shows its parts here — each with its plates, its
 * share, whether it has paid, its own print and (where the page offers it) its
 * own settle — plus the unclaimed-plates warning the old row carried, and one
 * link to the split sheet itself so re-marking and clearing stay reachable.
 */
export function PrintChoiceSheet({
  invoice,
  isSubmitting,
  canMarkParts,
  canPrintCheck = true,
  onPrintTicket,
  printTicketBusy = false,
  onWhole,
  onPrintPlates,
  onSettlePart,
  onEditParts,
  onCancel,
}: {
  invoice: Invoice;
  isSubmitting: boolean;
  /** `RESTAURANT_INVOICE_APPEND` — the picked plates become a part (`assignParty`). */
  canMarkParts: boolean;
  /**
   * May this session print a check here (an OPEN bill's `printCheck` is
   * `RESTAURANT_INVOICE_CLOSE`)? Without it only «طباعة التذكرة» is offered.
   */
  canPrintCheck?: boolean;
  /**
   * «طباعة التذكرة» — offered FIRST while an unprinted kitchen ticket holds the
   * close (2026-10-20); absent otherwise.
   */
  onPrintTicket?: () => void;
  printTicketBusy?: boolean;
  /** Today's whole-bill print. */
  onWhole: () => void;
  /** Print a check for these plates (the page marks the part first when it may). */
  onPrintPlates: (plateIds: string[], partyId: string | null) => void | Promise<void>;
  /** Take one part's share, where this till offers it; absent hides the control. */
  onSettlePart?: (plateIds: string[], partyId: string) => void;
  /** The split sheet (`PartySheet`) — re-mark, clear, name. */
  onEditParts?: () => void;
  onCancel: () => void;
}) {
  const t = useTranslate();
  const money = useMoney();
  const { locale } = useI18n();
  const [step, setStep] = useState<"choose" | "items">("choose");
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());

  const paid = useMemo(() => paidPlateIds(invoice), [invoice]);
  /* Nothing left to pay (the rest was taken as a typed amount, D126): no plate
     is owed for, so there is nothing to pick and nothing walking out unpaid. */
  const owed = outstandingOnBill(invoice) > 0;
  /* Plates still owed for: a part that is square is off every new check. */
  const plates = useMemo(
    () => (owed ? markablePlates(invoice).filter((item) => !paid.has(item.id)) : []),
    [invoice, paid, owed],
  );
  const shares = useMemo(() => partyShares(invoice), [invoice]);
  /*
   * WHAT A PICK MAY TAKE (gate F1/F2): only plates in NO part. A plate of a
   * part that has handed money over must never be re-marked (`settleRows`'
   * rule — it would be charged twice), and a plate of an unpaid part is not
   * moved silently either: moving it is «تعديل الأجزاء»'s job, and the part's
   * own print and «تحصيل» cover printing it. Those plates are listed disabled,
   * with their part's name.
   */
  const pickable = plates.filter((item) => item.partyId === null);
  const markable = markablePlates(invoice);
  const unclaimed = useMemo(() => unmarkedPlates(invoice), [invoice]);
  const unclaimedOwed = owed && unclaimed.length > 0;
  const partPlates = (partyId: string) =>
    plates.filter((item) => item.partyId === partyId).map((item) => item.id);

  const selectedIds = pickable.map((item) => item.id).filter((id) => selected.has(id));
  const selectedAmount =
    selectedIds.length > 0 ? liveOpenTotals(invoiceForPlates(invoice, selectedIds)).total : 0;
  const wholeAmount = invoice.status === "OPEN" ? liveOpenTotals(invoice).total : invoice.total;
  /* No 0.00 check (gate follow-up): a bill with nothing to charge, or a session
     that may not print one, keeps only «طباعة التذكرة». */
  const offersCheck = canPrintCheck && wholeAmount > 0;

  const toggle = (id: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  /*
   * THE SPLIT STATE in one line (B43) — the sentence the foot's row carried:
   * marked and complete, or marked with plates still nobody's. The second is
   * the one that matters (a dish walking out unpaid), so it alone takes the
   * warning colour.
   */
  const ways = t(
    countedKey(shares.length, {
      one: (m) => m.pos.partyWaysOne,
      two: (m) => m.pos.partyWaysTwo,
      few: (m) => m.pos.partyWaysFew,
      many: (m) => m.pos.partyWays,
    }),
    { count: shares.length },
  );
  const splitState =
    unclaimed.length === 0
      ? t(
          countedKey(shares.length, {
            one: (m) => m.pos.partyAllMarkedOne,
            two: (m) => m.pos.partyAllMarkedTwo,
            few: (m) => m.pos.partyAllMarkedFew,
            many: (m) => m.pos.partyAllMarked,
          }),
          { count: shares.length },
        )
      : t(
          countedKey(unclaimed.length, {
            one: (m) => m.pos.partySomeUnmarkedOne,
            two: (m) => m.pos.partySomeUnmarkedTwo,
            few: (m) => m.pos.partySomeUnmarkedFew,
            many: (m) => m.pos.partySomeUnmarked,
          }),
          { ways, plates: unclaimed.length },
        );

  const plateCount = (count: number) =>
    t(
      countedKey(count, {
        none: (m) => m.pos.partyPlateCountNone,
        one: (m) => m.pos.partyPlateCountOne,
        two: (m) => m.pos.partyPlateCountTwo,
        few: (m) => m.pos.partyPlateCountFew,
        many: (m) => m.pos.partyPlateCount,
      }),
      { count },
    );

  const printLabel =
    selectedIds.length === 0
      ? t((m) => m.printChoice.printPickedNone)
      : t(
          countedKey(selectedIds.length, {
            one: (m) => m.printChoice.printPickedOne,
            two: (m) => m.printChoice.printPickedTwo,
            few: (m) => m.printChoice.printPickedFew,
            many: (m) => m.printChoice.printPicked,
          }),
          { count: selectedIds.length, amount: money(selectedAmount) },
        );

  const editLink =
    onEditParts && markable.length > 0 ? (
      <button
        type="button"
        disabled={isSubmitting}
        data-testid="print-choice-edit-parts"
        data-ui="section-link"
        onClick={onEditParts}
        className="shrink-0 text-[length:var(--font-size-2xs)] font-extrabold text-[color:var(--info)] transition-colors hover:text-[color:var(--text)] disabled:opacity-40"
      >
        {t((m) => m.printChoice.editParts)}
      </button>
    ) : null;

  return (
    <Modal
      isOpen
      onClose={onCancel}
      size="sm"
      title={t((m) => m.printChoice.title)}
      closeLabel={t((m) => m.common.close)}
      data-testid="print-choice-sheet"
      footerActions={
        step === "items" ? (
          <>
            <Button
              variant="outline"
              size="touch"
              disabled={isSubmitting}
              onClick={() => setStep("choose")}
              data-testid="print-choice-back"
            >
              {t((m) => m.common.back)}
            </Button>
            <Button
              variant="primary"
              size="touch"
              className="font-extrabold tabular-nums"
              disabled={selectedIds.length === 0 || isSubmitting}
              isLoading={isSubmitting}
              leadingIcon={<Printer size={16} strokeWidth={1.8} aria-hidden="true" />}
              data-testid="print-choice-print-items"
              onClick={() => {
                if (selectedIds.length === 0) return;
                void onPrintPlates(selectedIds, null);
              }}
            >
              {printLabel}
            </Button>
          </>
        ) : (
          <Button variant="outline" size="touch" onClick={onCancel} data-testid="print-choice-cancel">
            {t((m) => m.common.cancel)}
          </Button>
        )
      }
    >
      {step === "choose" ? (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2">
            {onPrintTicket ? (
              <ChoiceButton
                testId="print-choice-ticket"
                icon={<ChefHat size={18} strokeWidth={1.8} aria-hidden="true" />}
                label={t((m) => m.pos.printChit)}
                hint={t((m) => m.printChoice.ticketHint)}
                disabled={isSubmitting || printTicketBusy}
                attention
                onClick={onPrintTicket}
              />
            ) : null}
            {offersCheck ? (
            <ChoiceButton
              testId="print-choice-whole"
              icon={<ReceiptText size={18} strokeWidth={1.8} aria-hidden="true" />}
              label={t((m) => m.printChoice.whole)}
              hint={t((m) => m.printChoice.wholeHint, { amount: money(wholeAmount) })}
              disabled={isSubmitting}
              onClick={onWhole}
            />
            ) : null}
            {offersCheck && pickable.length > 0 ? (
              <ChoiceButton
                testId="print-choice-items"
                icon={<ListChecks size={18} strokeWidth={1.8} aria-hidden="true" />}
                label={t((m) => m.printChoice.items)}
                hint={
                  canMarkParts
                    ? t((m) => m.printChoice.itemsHint)
                    : t((m) => m.printChoice.itemsHintPrintOnly)
                }
                disabled={isSubmitting}
                onClick={() => setStep("items")}
              />
            ) : null}
          </div>

          {/* F3b — name and mark the families without printing any paper: the
              split sheet, reachable before any part exists. */}
          {shares.length === 0 && editLink ? (
            <div className="flex justify-end">{editLink}</div>
          ) : null}

          {/* THE SPLIT, WHEN THERE IS ONE — what the old foot row said, and the
              parts themselves. */}
          {shares.length > 0 ? (
            <section className="flex flex-col gap-1.5" data-testid="print-choice-parts">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-[length:var(--font-size-2xs)] font-extrabold uppercase tracking-[0.06em] text-[color:var(--text-disabled)]">
                  {t((m) => m.printChoice.partsTitle)}
                </span>
                {editLink}
              </div>
              <ul className="flex flex-col gap-1">
                {shares.map((share) => {
                  const ids = partPlates(share.id);
                  return (
                    <li
                      key={share.id}
                      className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 rounded-xl bg-[color:var(--surface-alt)] px-3 py-2"
                      data-testid={`print-choice-part-${share.id}`}
                    >
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate text-[length:calc(var(--font-size-xs)_+_0.5px)] font-bold">
                          <bdi>{share.label}</bdi>
                        </span>
                        <span className="flex flex-wrap items-baseline gap-x-2 text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-semibold text-[color:var(--text-muted)]">
                          <span>{plateCount(share.itemCount)}</span>
                          <span className="tabular-nums font-extrabold text-[color:var(--text)]">
                            {share.amount === null ? "—" : money(share.amount)}
                          </span>
                          <span
                            data-testid={`print-choice-part-status-${share.id}`}
                            className={
                              share.paid > 0
                                ? "font-extrabold text-[color:var(--success,var(--info))]"
                                : undefined
                            }
                          >
                            {share.isSettled
                              ? t((m) => m.pos.partialPartyPaid)
                              : share.paid > 0
                                ? t((m) => m.pos.partialPartyPaidOf, {
                                    paid: money(share.paid),
                                    amount: share.amount === null ? "—" : money(share.amount),
                                  })
                                : t((m) => m.printChoice.partUnpaid)}
                          </span>
                        </span>
                      </span>
                      {ids.length > 0 ? (
                        <span className="flex shrink-0 items-center gap-1.5">
                          {onSettlePart && share.paid === 0 ? (
                            <Button
                              variant="outline"
                              size="touch"
                              disabled={isSubmitting}
                              data-testid={`print-choice-part-settle-${share.id}`}
                              onClick={() => onSettlePart(ids, share.id)}
                            >
                              {t((m) => m.printChoice.partSettle)}
                            </Button>
                          ) : null}
                          <Button
                            variant="outline"
                            size="touch"
                            disabled={isSubmitting}
                            leadingIcon={<Printer size={16} strokeWidth={1.8} aria-hidden="true" />}
                            aria-label={t((m) => m.printChoice.partPrint, { label: share.label })}
                            title={t((m) => m.printChoice.partPrint, { label: share.label })}
                            data-testid={`print-choice-part-print-${share.id}`}
                            onClick={() => void onPrintPlates(ids, share.id)}
                          />
                        </span>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
              {/* The line that stops a dish walking out unpaid (B43). */}
              <p
                className={
                  unclaimedOwed
                    ? "rounded-xl border border-[color:var(--warning)] px-3 py-2 text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-bold text-[color:var(--warning)]"
                    : "text-[length:var(--font-size-2xs)] font-semibold text-[color:var(--text-muted)]"
                }
                data-testid="print-choice-split-state"
                data-unclaimed={unclaimedOwed ? "" : undefined}
              >
                {splitState}
              </p>
            </section>
          ) : null}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <span className="text-xs font-bold text-[color:var(--text-muted)]">
            {t((m) => m.pos.partyPickPlates)}
          </span>
          <ul className="flex max-h-[44svh] flex-col gap-1 overflow-y-auto" data-testid="print-choice-plates">
            {plates.map((item) => {
              const inPart = item.partyId !== null;
              const checked = !inPart && selected.has(item.id);
              const owner = shares.find((share) => share.id === item.partyId)?.label ?? null;
              const name = plateNameForLocale(item, locale);
              return (
                <li key={item.id}>
                  <label
                    data-in-part={inPart ? "" : undefined}
                    className={[
                      "flex items-start gap-2.5 rounded-xl border px-2.5 py-2 transition-colors",
                      inPart ? "cursor-default opacity-50" : "cursor-pointer",
                      checked
                        ? "border-[color:var(--accent)] bg-[color:var(--surface-alt)]"
                        : "border-[color:var(--border-light)]",
                    ].join(" ")}
                  >
                    <Checkbox
                      checked={checked}
                      disabled={inPart}
                      onChange={() => {
                        if (!inPart) toggle(item.id);
                      }}
                      data-testid={`print-choice-plate-${item.id}`}
                      aria-label={name}
                    />
                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <span className="flex items-baseline justify-between gap-2">
                        <span className="truncate text-sm font-semibold">{name}</span>
                        <span className="tabular-nums text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-bold text-[color:var(--text-muted)]">
                          {money(item.lineTotal)}
                        </span>
                      </span>
                      {owner ? (
                        <span className="truncate text-[length:var(--font-size-2xs)] font-bold text-[color:var(--accent)]">
                          <bdi>{owner}</bdi>
                        </span>
                      ) : null}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </Modal>
  );
}

function ChoiceButton({
  testId,
  icon,
  label,
  hint,
  disabled,
  attention = false,
  onClick,
}: {
  testId: string;
  icon: React.ReactNode;
  label: string;
  hint: string;
  disabled: boolean;
  /** The choice the bill is waiting on — drawn in the warning ink. */
  attention?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      data-testid={testId}
      onClick={onClick}
      data-attention={attention ? "" : undefined}
      className={`flex min-h-[var(--touch-target,44px)] items-start gap-3 rounded-xl border ${attention ? "border-[color:var(--warning)]" : "border-[color:var(--border-light)]"} px-3 py-2.5 text-start transition-colors hover:border-[color:var(--accent)] hover:bg-[color:var(--surface-alt)] disabled:opacity-40`}
    >
      <span className={`mt-0.5 shrink-0 ${attention ? "text-[color:var(--warning)]" : "text-[color:var(--text-muted)]"}`}>{icon}</span>
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="text-sm font-extrabold">{label}</span>
        <span className="text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-semibold text-[color:var(--text-muted)] tabular-nums">
          {hint}
        </span>
      </span>
    </button>
  );
}
